import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Shield, Heart, HeartHandshake, GraduationCap } from "lucide-react";
import { IMAGES, ORG, HOTEL_2026_PRESIDENTS } from "@/lib/constants";
import AnniversaryCountdown from "@/components/AnniversaryCountdown";
import SectionHeading from "@/components/SectionHeading";
import PageMeta from "@/components/PageMeta";

export default function Home() {
  return (
    <div data-testid="home-page">
      <PageMeta
        title="Detroit Firefighter Advocacy & Service Since 1967"
        description="The Phoenix of the Detroit Fire Department — a 501(c)(3) nonprofit. 60th Anniversary in 2027 · 2027 NCR Spring Conference · Historic Engine 11 · Project 100 · community outreach across Detroit."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden" data-testid="hero">
        <img
          src={IMAGES.hero}
          alt="Phoenix rising from embers"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "30% center" }}
        />
        {/* Wave 3 — Home hero Option B: soft dimmer keeps the community photo warm but prevents single-face dominance */}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 hero-vignette" />
        {/* marquee ribbon */}
        <div className="hidden lg:block absolute top-24 inset-x-0 overflow-hidden opacity-30 pointer-events-none">
          <div className="marquee-track font-display italic text-white/30 text-xl lg:text-3xl">
            {Array(2).fill(0).map((_, i) => (
              <span key={i} className="mx-12">
                Brotherhood · Sisterhood · Service · Legacy · Detroit · Engine 11 · Phoenix Rising · Est. 1967 ·
              </span>
            ))}
          </div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-5 lg:px-8 pt-28 sm:pt-32 lg:pt-0 pb-16 lg:pb-24">
          <Link
            to="/2027"
            data-testid="hero-anniversary-badge"
            className="inline-flex items-center gap-3 rise rounded-full pl-2 pr-5 py-2 bg-amber-500/10 border border-amber-500/40 backdrop-blur-md hover:bg-amber-500/20 transition-colors group"
          >
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-black">
              <Flame className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span className="font-accent text-[11px] sm:text-xs tracking-[0.28em] uppercase text-amber-400">
              Celebrating 60 Years in 2027
            </span>
            <ArrowUpRight className="h-3.5 w-3.5 text-amber-400 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <h1 className="font-display text-[2rem] sm:text-6xl lg:text-8xl leading-[1.08] sm:leading-[1.02] lg:leading-[0.95] tracking-tight mt-4 sm:mt-5 text-white max-w-5xl rise-2">
            A Voice. A Legacy.
            <span className="block text-amber-500">A Phoenix Rising.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-slate-200 text-base lg:text-xl leading-relaxed rise-3">
            As we prepare to celebrate 60 years of service, legacy, advocacy, and
            community impact, the Phoenix of the Detroit Fire Department continues to
            stand as a voice for minorities in the fire service, a steward of Historic
            Engine 11, and a trusted partner to the Detroit community.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3 rise-4">
            <Link
              to="/2027"
              className="btn-gold w-full sm:w-auto justify-center text-base px-7 py-3.5 shadow-[0_18px_60px_-18px_rgba(217,119,6,0.9)]"
              data-testid="hero-60th-btn"
            >
              Explore 2027 Conference &amp; 60th Anniversary <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CHAPTER-PRESIDENT-FACING + 60TH ANNIVERSARY — chronological order (2026 first, 2027 second) */}
      <section className="relative py-16 lg:py-24" data-testid="home-2026-presidents">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-black via-[#0E0E0E] to-red-950/40 p-6 lg:p-10">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
              <div className="flex-1 min-w-0">
                <div className="overline text-amber-500">For Chapter Presidents · Coming First</div>
                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 text-white leading-tight">
                  2026 NCR Presidents Meeting
                  <span className="block text-amber-300 text-lg sm:text-xl lg:text-2xl mt-1 font-normal">
                    October 8–10, 2026 · Detroit
                  </span>
                </h3>
                <p className="text-slate-300 leading-relaxed mt-4 max-w-2xl">
                  Phoenix is hosting the North Central Region Presidents Meeting
                  this October — the regional leadership gathering ahead of next
                  year's NCR Spring Conference. Booking, group code, and travel
                  details available now.
                </p>
              </div>
              <div className="lg:w-auto flex-shrink-0">
                <Link
                  to="/2027#ncr-presidents-2026"
                  className="btn-ghost inline-flex items-center justify-center text-sm"
                  data-testid="home-2026-presidents-details"
                >
                  Meeting Details
                </Link>
                <a
                  href={HOTEL_2026_PRESIDENTS.bookingLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-gold inline-flex items-center justify-center"
                  data-testid="home-2026-presidents-cta"
                >
                  Book 2026 Room Block <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 60TH ANNIVERSARY FEATURE */}
      <section className="relative py-16 lg:py-32" data-testid="home-60th-feature">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="overline">2027 · Detroit · Milestone</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight mt-3 text-white">
              60 Years.
              <span className="block text-amber-500">One Phoenix.</span>
            </h2>
            <div className="divider-gold" />
            <p className="text-slate-300 text-lg max-w-xl leading-relaxed">
              In May 2027, the Phoenix of the Detroit Fire Department will host the
              2027 North Central Region Spring Conference (May 12–16) in Detroit.
              As part of this historic conference, Phoenix will present its 60th
              Anniversary Gala: The Detroit Renaissance Ball — the signature
              anniversary celebration honoring six decades of legacy and service.
            </p>
            <div className="mt-8">
              <AnniversaryCountdown />
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/2027" className="btn-gold" data-testid="60th-feature-cta">
                Explore the Celebration <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden ring-ember bg-black">
              <img
                src={IMAGES.anniversaryBall}
                alt="2027 NCR Spring Conference Save the Date — Still We Rise: 60 Years · May 12-16, 2027 · Host Hotel Greektown Hollywood Casino"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS — 4 OFFICIAL PHOENIX PILLARS (matches /about) */}
      <section className="py-16 lg:py-32 bg-[#0E0E0E]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading
            overline="Our Four Pillars"
            title="The work of the Phoenix."
            subtitle="Four pillars shape every program, partnership, and project the Phoenix carries forward — from preparing the next generation of Detroit firefighters to standing with the communities we serve."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: GraduationCap,
                title: "Professional Development",
                text: "Supporting the continued growth, leadership, training, and advancement of Phoenix members throughout their fire service careers.",
                to: "/programs-outreach",
              },
              {
                icon: Heart,
                title: "Health & Wellness",
                text: "Caring for the well-being of Phoenix members — active and retired — through active and retired firefighter fellowship, family support, and connection to the Phoenix community.",
                to: "/membership",
              },
              {
                icon: Shield,
                title: "Equitable Inclusion",
                text: "Advocating for representation, opportunity, and access within the Detroit Fire Department, and serving as a voice for minority firefighters and the communities of Detroit.",
                to: "/about",
              },
              {
                icon: HeartHandshake,
                title: "Community Engagement",
                text: "Serving Detroit through the annual backpack giveaway, senior feeding and fellowship, support for families impacted by fire, and stewardship of Historic Engine 11.",
                to: "/programs-outreach",
              },
            ].map((p) => (
              <Link key={p.title} to={p.to} className="card-dark p-8 block group" data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <p.icon className="h-7 w-7 text-amber-500" />
                <h3 className="font-display text-2xl text-white mt-5">{p.title}</h3>
                <p className="text-slate-400 mt-3 leading-relaxed text-sm">{p.text}</p>
                <div className="mt-6 text-amber-500 text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more <ArrowUpRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ENGINE 11 SECTION */}
      <section className="py-16 lg:py-32" data-testid="home-engine11">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="overline">Headquarters</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight mt-3 text-white">Historic Engine 11.</h2>
            <div className="divider-gold" />
            <p className="text-slate-300 leading-relaxed text-lg max-w-xl">
              A landmark Detroit firehouse and the heart of the Phoenix.
              Engine 11 is a symbol of preservation, of service that spans generations,
              and of a city that never stops rising.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/historic-engine-11" className="btn-primary">
                Visit Engine 11
              </Link>
              <Link to="/rent-engine-11" className="btn-ghost">
                Rent the Firehouse
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden ring-ember">
              <img src={IMAGES.engine11} alt="Historic Engine 11" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* PROJECT 100 BANNER */}
      <section className="relative py-16 lg:py-32 overflow-hidden" data-testid="home-project100">
        <img src={IMAGES.project100} alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-black/10 lg:from-black/40 lg:via-black/20 lg:to-black/5" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="overline">Project 100</div>
            <h2 className="font-display text-4xl lg:text-6xl tracking-tight mt-3 text-white leading-[1.02]">
              Building the next 100 firefighters.
            </h2>
            <div className="divider-gold" />
            <p className="text-slate-300 leading-relaxed text-lg max-w-2xl">
              Project 100 is our recruitment, mentorship, and pathway initiative — opening doors
              to Detroit Fire Department careers and shaping leaders who reflect the city they serve.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/programs-outreach#project-100" className="btn-gold" data-testid="home-project100-cta">
              Learn About Project 100 <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* OUTREACH */}
      <section className="py-16 lg:py-32 bg-[#0E0E0E]" data-testid="home-outreach">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="Community Outreach"
              title="We show up — in every neighborhood, every season."
              subtitle="From the annual backpack giveaway to senior feeding and fellowship, support for families impacted by fire, Project 100 mentorship, and developing fellowship opportunities for active members and their families — Phoenix shows up where it counts."
            />
            <Link to="/programs-outreach" className="btn-primary mt-2" data-testid="home-outreach-cta">
              See Our Programs <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {[
              "Annual Backpack Giveaway",
              "Senior Feeding & Fellowship",
              "Families Impacted by Fire",
              "Project 100 Mentorship",
              "Retiree Fellowship",
              "Active Member & Family Fellowship",
            ].map((label, i) => (
              <div key={label} className="card-dark p-6">
                <div className="font-accent text-amber-500 text-sm tracking-[0.28em] uppercase">Program {String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-2xl text-white mt-3">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DONATE CTA */}
      <section className="py-16 lg:py-32" data-testid="home-donate-cta">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden ring-ember p-10 lg:p-16">
            <img src={IMAGES.homeStatBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
            <div className="absolute inset-0 bg-gradient-to-br from-black/55 via-black/40 to-red-950/35" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <div className="overline">Stand With Us</div>
                <h2 className="font-display text-4xl lg:text-6xl tracking-tight text-white mt-3 leading-[1.02]">
                  Fuel the Phoenix.<br />
                  Power the next chapter.
                </h2>
                <p className="text-slate-300 mt-5 max-w-xl leading-relaxed">
                  Your support sustains Phoenix programs, Historic Engine 11
                  preservation, community outreach, Project 100, families
                  impacted by fire, senior feeding and fellowship, the annual
                  backpack giveaway, retiree fellowship, and active member and
                  family fellowship. Volunteer. Show up. Stand with the Phoenix.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link to="/donate-sponsor" className="btn-gold w-full justify-center" data-testid="home-cta-donate">
                  Support Our Mission
                </Link>
                <Link to="/2027" className="btn-ghost w-full justify-center" data-testid="home-cta-2027">
                  Explore 2027 Conference &amp; 60th Anniversary
                </Link>
                <Link to="/contact" className="btn-ghost w-full justify-center">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
